import { randomId } from "../../shared/randomId";
import type { WorkPackage } from "./agentExecutionContract";
import { assertValidWorkPackages } from "./workPackageContract";

import type {
  ExecutionProgramRepository,
  PersistentExecutionProgram,
} from "./executionProgramState";

export interface LaunchExecutionProgramInput {
  missionId?: string;

  objective: string;

  requestedBy: string;

  workPackages: readonly WorkPackage[];

  conversationId?: string;

  projectId?: string;

  repository?: string;

  branch?: string;

  headSha?: string;
}

export class CyanProgramService {
  constructor(private readonly programs: ExecutionProgramRepository) {}

  async launch(input: LaunchExecutionProgramInput) {
    if (!input.objective.trim()) {
      throw new Error("Execution program objective is required.");
    }

    /*
     * Durable program creation must reject malformed WorkPackages before
     * they can reach execution_jobs.
     *
     * TypeScript types are compile-time only. Runtime validation protects
     * database and queue invariants even when input comes from external
     * services, JSON, migrations, recovery flows or administrative tools.
     */
    assertValidWorkPackages(input.workPackages);

    const now = new Date().toISOString();

    const program: PersistentExecutionProgram = {
      id: input.missionId ?? randomId(),

      objective: input.objective,

      state: "READY",

      createdAt: now,

      updatedAt: now,

      currentWave: 0,

      requestedBy: input.requestedBy,

      conversationId: input.conversationId,

      projectId: input.projectId,

      repository: input.repository,

      branch: input.branch,

      headSha: input.headSha,

      workItems: input.workPackages.map((workItem) => ({
        workItem,
        state: workItem.dependencies.length ? "PENDING" : "READY",
        evidence: [],
      })),
    };

    await this.programs.create(program);

    return program;
  }
}
